import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api } from './api.js';

export type TicketKind = 'entry' | 'combo' | 'seat' | 'consumption';
export type Locale = 'es' | 'en';

export interface DashboardTicketType {
  id: string;
  kind: TicketKind;
  priceCents: number;
  capacity: number;
  sold: number;
  maxPerOrder: number | null;
  hidden: boolean;
  sortOrder: number;
  validityFrom: string | null;
  validityTo: string | null;
  translations: Record<string, { name: string; description: string }>;
}

export interface CreateTicketTypePayload {
  kind: TicketKind;
  priceCents: number;
  capacity: number;
  maxPerOrder: number | null;
  hidden: boolean;
  translations: Record<string, { name: string; description: string }>;
  validityFrom?: string;
  validityTo?: string;
  comboItems?: string[];
}

export interface UpdateTicketTypePayload {
  priceCents?: number;
  capacity?: number;
  maxPerOrder?: number | null;
  hidden?: boolean;
  sortOrder?: number;
  validityFrom?: string | null;
  validityTo?: string | null;
  translations?: Record<string, { name: string; description: string }>;
}

/**
 * Hook helper para mantener la lista de ticket-types en sync con el query
 * de `['event', eventId]`. Centraliza el invalidate, así no se nos olvida
 * en cada call site y la UI no se desincroniza después de un create/edit.
 */
function useTicketsInvalidator(eventId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['event', eventId] });
}

export function useCreateTicketType(
  eventId: string,
): UseMutationResult<{ id: string }, Error, CreateTicketTypePayload> {
  const invalidate = useTicketsInvalidator(eventId);
  return useMutation({
    mutationFn: (payload: CreateTicketTypePayload) =>
      api<{ id: string }>(`/events/${eventId}/ticket-types`, {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTicketType(
  eventId: string,
): UseMutationResult<{ ok: true }, Error, { id: string; payload: UpdateTicketTypePayload }> {
  const invalidate = useTicketsInvalidator(eventId);
  return useMutation({
    mutationFn: ({ id, payload }) =>
      api<{ ok: true }>(`/events/${eventId}/ticket-types/${id}`, {
        method: 'PATCH',
        body: payload,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTicketType(
  eventId: string,
): UseMutationResult<{ ok: true }, Error, string> {
  const invalidate = useTicketsInvalidator(eventId);
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/events/${eventId}/ticket-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(),
  });
}
